import type { PromoCandidate } from '@supply-mover-2-0/shared';
import { PromoTile } from './PromoTile';

/**
 * Three-across header tile-row, designed to replace a single big 4:1
 * hero. Same Weee pattern: three medium-sized banners that read as a
 * row on desktop and as a swipeable peek-carousel on mobile.
 *
 * Mobile (< md): horizontal-scroll snap row. Each tile is ~85% of the
 * viewport width with the next tile peeking from the right, so the
 * customer sees there's more to swipe to. Stacking the same three
 * tiles single-column at 100% width (the earlier behaviour) ate
 * roughly 600px of vertical real estate on a 390px screen and
 * pushed everything else below the fold; the peek-carousel keeps
 * the same three offers in a single ~150px-tall band.
 *
 * Desktop (md+): regular 3-col grid. The desktop screen has the
 * horizontal room to display three full tiles side-by-side without
 * any swipe affordance.
 */
export function PromoThreeUpWidget({
	promos,
	density,
	onLikelyStaleUrl,
}: {
	promos: Array<PromoCandidate>;
	density?: 'standard' | 'compact';
	/** Forwarded to each PromoTile. See `PromoTile.onLikelyStaleUrl`. */
	onLikelyStaleUrl?: () => void;
}): JSX.Element | null {
	const slice = promos.slice(0, 3);
	if (slice.length === 0) return null;
	return (
		<>
			{/* Mobile: peek-carousel. `overflow-x-auto` + `snap-x
			    snap-mandatory` lets the customer swipe between tiles
			    one at a time. We bleed past the page's px-4 gutter on
			    the RIGHT only (`-mr-4 pr-4`) so the next tile peeks
			    from beyond the right gutter, while the LEFT respects
			    the dashboard's gutter — without that, the leftmost
			    visible tile (mid-scroll, this could be tile 2 or 3)
			    sat flush against the viewport edge and the rounded
			    corner read as cut-off. */}
			<div className="md:hidden -mr-4 pr-4 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1">
				{slice.map((p) => (
					<div key={p.id} className="snap-start shrink-0 w-[85%]">
						<PromoTile
							promo={p as never}
							size="slate"
							density={density}
							onLikelyStaleUrl={onLikelyStaleUrl}
						/>
					</div>
				))}
			</div>
			{/* Desktop: regular 3-col grid. */}
			<div className="hidden md:grid md:grid-cols-3 gap-3 w-full h-full">
				{slice.map((p) => (
					<PromoTile
						key={p.id}
						promo={p as never}
						size="slate"
						density={density}
						onLikelyStaleUrl={onLikelyStaleUrl}
					/>
				))}
			</div>
		</>
	);
}
